/**
 * Base file to handle Axios via different dynamic properties
 * which in turns make this library as a universal solution
 *
 * Author: Poojan Pradhan
 */

import { accessToken } from "./tokenService";
import axiosRetry from "axios-retry";
import axios from "axios";

import { logError } from "./logService";
import { ReadOnlyProps } from "../../utils/templates";
import store from "../../redux/configureStore";
import { adminAuthError } from "../../redux/reducers/adminReducer";

interface httpProps {
  baseURL?: string;
  type: string;
  path: string;
  body?: any;
  contentType?: string;
  isTokenRequired?: boolean;
  isAcceptHeaderRequired?: boolean;
  timeout?: number;
}

export const adminHttpMStepsClient = async ({
  baseURL,
  type,
  path,
  body,
  contentType = "application/json",
  isTokenRequired = true,
  isAcceptHeaderRequired = true,
  timeout = 60000, // Increased default timeout to 60s
}: ReadOnlyProps<httpProps>) => {
  
  const headers: Record<string, string> = {
    "Content-Type": contentType,
  };

  if (isAcceptHeaderRequired) {
    headers["Accept"] = "application/json";
  }

  if (isTokenRequired) {
    headers["Authorization"] = `Bearer ${
      sessionStorage.getItem("accessAdminToken") || ""
    }`;
  }

  // Create a new axios instance to avoid global pollution
  const client = axios.create({
    baseURL,
    headers,
    timeout,
  });

  // Configure retry logic
  axiosRetry(client, { 
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      // Retry on network errors or 5xx status codes
      return axiosRetry.isNetworkOrIdempotentRequestError(error) || (error.response?.status ? error.response.status >= 500 : false);
    }
  });

  //Axios Interceptors
  client.interceptors.response.use(
    (response) => {
      if (response.status === 401) {
        logError("You are not authorized");
        sessionStorage.clear();
      }
      return response;
    },
    (error) => {
      if (error.status === 401) {
        logError("You are not authorized");
        store.dispatch(adminAuthError());
      }
      const expectedError =
        error &&
        error.response &&
        error.response.status >= 400 &&
        error.response.status < 500;

      if (!expectedError) {
        logError(
          error?.response?.data?.message ||
            error?.message ||
            error ||
            "An error occurred"
        );
      }

      return Promise.reject(
        error?.response?.data?.message ||
          error?.message ||
          error ||
          "An error occurred"
      );
    }
  );

  let axiosMethod: any = null;
  switch (type) {
    case "GET":
      axiosMethod = client.get(path);
      break;
    case "POST":
      axiosMethod = client.post(path, body);
      break;
    case "PUT":
      axiosMethod = client.put(path, body);
      break;
    case "PATCH":
      axiosMethod = client.patch(path, body);
      break;
    case "DELETE":
      axiosMethod = client.delete(path);
      break;
  }

  return axiosMethod;
};
